/**
 * FloatingDiceLibrary — in-game pull-out window for managing equipped dice.
 *
 * Does NOT duplicate the dice UI: it BORROWS the live DiceScreen content
 * (`.dice-screen-content` + its drag ghost), re-parents it into a floating
 * panel for the duration, hides the lobby-only TRY button, and on close
 * returns both nodes to exactly where they came from and re-shows TRY. This
 * keeps a single source of truth for owned dice / presets / drag-to-slot.
 *
 * Mirrors the StandaloneChat floating-overlay idiom: mounted into the in-game
 * `#ui-container`, slides in via a `.visible` class, pointer-events re-enabled
 * on the panel (the container itself is pointer-events:none).
 *
 * z-index: scrim 655 / panel 660 (above toasts at 600, below the chat bubble
 * at 700 and coach marks at 760).
 */

import { EventEmitter } from '../../engine/core/EventEmitter';

const STYLE_ID = 'sdl-dicelib-styles';

interface FloatingDiceLibraryEvents {
  'close': void;
}

export interface FloatingDiceLibraryOptions {
  container: HTMLElement;
}

/** What to borrow + where it came from, so close() can restore it exactly. */
interface BorrowedNode {
  el: HTMLElement;
  parent: HTMLElement;
  nextSibling: Node | null;
}

export class FloatingDiceLibrary extends EventEmitter<FloatingDiceLibraryEvents> {
  public element: HTMLElement;
  private scrim: HTMLElement;
  private body: HTMLElement;
  private container: HTMLElement;
  private open = false;

  private borrowed: BorrowedNode[] = [];
  /** The TRY button we hid on open, and its prior inline display value. */
  private hiddenTry: { el: HTMLElement; prevDisplay: string } | null = null;
  private restoreTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: FloatingDiceLibraryOptions) {
    super();
    this.container = options.container;
    this.injectStyles();

    this.scrim = document.createElement('div');
    this.scrim.className = 'sdl-dicelib-scrim';
    this.scrim.addEventListener('pointerdown', () => this.close());

    this.element = document.createElement('div');
    this.element.className = 'sdl-dicelib';
    this.element.innerHTML = `
      <button class="sdl-dicelib-close" aria-label="Close">&times;</button>
      <div class="sdl-dicelib-body"></div>
    `;
    this.body = this.element.querySelector('.sdl-dicelib-body')!;
    this.element.querySelector('.sdl-dicelib-close')!
      .addEventListener('click', () => this.close());

    this.container.appendChild(this.scrim);
    this.container.appendChild(this.element);
  }

  isOpen(): boolean {
    return this.open;
  }

  /**
   * Open the window around borrowed DiceScreen content.
   * @param content the `.dice-screen-content` element to host
   * @param extra   any sibling nodes to bring along (e.g. the drag ghost)
   */
  show(content: HTMLElement, extra: HTMLElement[] = []): void {
    if (this.open) return;
    this.open = true;
    if (this.restoreTimer) { clearTimeout(this.restoreTimer); this.restoreTimer = null; }

    // Hide the lobby-only TRY button.
    const tryBtn = content.querySelector<HTMLElement>('.dice-try-btn');
    if (tryBtn) {
      this.hiddenTry = { el: tryBtn, prevDisplay: tryBtn.style.display };
      tryBtn.style.display = 'none';
    }

    // Borrow content into the (transformed) panel body. Extras (the drag
    // ghost) go into the NON-transformed container instead: the ghost is
    // position:fixed and would otherwise resolve relative to the panel's
    // transform, landing offset from the pointer. Its z-index (9999) keeps it
    // above the panel even as a container child.
    this.borrowed = [];
    this.borrowNode(content, this.body);
    for (const node of extra) this.borrowNode(node, this.container);

    // Slide in.
    requestAnimationFrame(() => {
      this.scrim.classList.add('visible');
      this.element.classList.add('visible');
    });
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.scrim.classList.remove('visible');
    this.element.classList.remove('visible');

    // Restore borrowed nodes after the slide-out finishes.
    this.restoreTimer = setTimeout(() => {
      this.restoreTimer = null;
      this.restoreBorrowed();
    }, 260);

    this.emit('close', undefined as void);
  }

  /** Force-restore immediately (game teardown) — no animation wait. */
  forceRestore(): void {
    if (this.restoreTimer) { clearTimeout(this.restoreTimer); this.restoreTimer = null; }
    this.open = false;
    this.scrim.classList.remove('visible');
    this.element.classList.remove('visible');
    this.restoreBorrowed();
  }

  private borrowNode(el: HTMLElement, into: HTMLElement): void {
    const parent = el.parentElement;
    if (!parent) return; // not mounted — nothing to restore to
    this.borrowed.push({ el, parent, nextSibling: el.nextSibling });
    into.appendChild(el);
  }

  private restoreBorrowed(): void {
    for (const b of this.borrowed) {
      // Re-insert at the original position if the anchor is still valid.
      if (b.nextSibling && b.nextSibling.parentNode === b.parent) {
        b.parent.insertBefore(b.el, b.nextSibling);
      } else {
        b.parent.appendChild(b.el);
      }
    }
    this.borrowed = [];
    if (this.hiddenTry) {
      this.hiddenTry.el.style.display = this.hiddenTry.prevDisplay;
      this.hiddenTry = null;
    }
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-dicelib-scrim {
        position: fixed;
        inset: 0;
        z-index: 655;
        background: rgba(0, 0, 0, 0.55);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.26s ease;
        backdrop-filter: blur(2px);
        -webkit-backdrop-filter: blur(2px);
      }
      .sdl-dicelib-scrim.visible {
        opacity: 1;
        pointer-events: auto;
      }

      .sdl-dicelib {
        position: fixed;
        left: 50%;
        bottom: 0;
        width: min(460px, 100vw);
        height: min(82vh, 720px);
        z-index: 660;
        background: rgba(8, 8, 10, 0.97);
        border: 1px solid rgba(0, 245, 255, 0.18);
        border-bottom: none;
        border-radius: 20px 20px 0 0;
        overflow: hidden;
        box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.7), 0 0 16px rgba(0, 245, 255, 0.12);
        backdrop-filter: blur(14px);
        -webkit-backdrop-filter: blur(14px);

        transform: translate(-50%, 100%);
        transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
        pointer-events: none;
      }
      .sdl-dicelib.visible {
        transform: translate(-50%, 0);
        pointer-events: auto;
      }

      .sdl-dicelib-body {
        width: 100%;
        height: 100%;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }
      /* Borrowed DiceScreen content fills the sheet (it sizes for full-screen
         in the lobby; here it just flows inside the scroll body). */
      .sdl-dicelib-body > .dice-screen-content {
        height: auto;
        min-height: 100%;
      }

      .sdl-dicelib-close {
        position: absolute;
        top: 8px;
        right: 10px;
        z-index: 2;
        width: 34px;
        height: 34px;
        border: none;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.08);
        color: #fff;
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        pointer-events: auto;
      }
      .sdl-dicelib-close:active { transform: scale(0.92); }
    `;
    document.head.appendChild(style);
  }
}
